(function () {
  const STORAGE_KEY = 'zc_attribution';

  /*
   * Tracking parameters accepted from landing-page URLs.
   *
   * Standard:
   *   utm_source
   *   utm_medium
   *   utm_campaign
   *   utm_content
   *   utm_term
   *
   * Meta:
   *   fbclid
   *   campaign_id
   *   adset_id
   *   ad_id
   */
  const TRACKING_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'fbclid',
    'campaign_id',
    'adset_id',
    'ad_id'
  ];

  /**
   * Read supported tracking parameters from the current URL.
   */
  function getTrackingParams() {
    const params = new URLSearchParams(window.location.search);
    const result = {};

    TRACKING_PARAMS.forEach(key => {
      const value = params.get(key);

      if (value) {
        result[key] = value;
      }
    });

    return result;
  }

  /**
   * Returns true if the URL contains at least one
   * supported tracking parameter.
   */
  function hasTrackingParams(data) {
    return Object.keys(data).length > 0;
  }

  /**
   * Read previously stored attribution.
   */
  function getStoredAttribution() {
    try {
      return JSON.parse(
        localStorage.getItem(STORAGE_KEY)
      ) || {};
    } catch (error) {
      console.error(
        'Zero Center attribution: unable to read localStorage',
        error
      );

      return {};
    }
  }

  /**
   * Save attribution to localStorage.
   */
  function saveStoredAttribution(data) {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(data)
      );
    } catch (error) {
      console.error(
        'Zero Center attribution: unable to write localStorage',
        error
      );
    }
  }

  /**
   * Create one attribution touch from the current visit.
   */
  function createTouch(params) {
    return {
      source: params.utm_source || '',
      medium: params.utm_medium || '',
      campaign: params.utm_campaign || '',
      campaign_id: params.campaign_id || '',
      adset_id: params.adset_id || '',
      ad_id: params.ad_id || '',
      content: params.utm_content || '',
      term: params.utm_term || '',
      fbclid: params.fbclid || '',
      landing_page: window.location.href,
      referrer: document.referrer || '',
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Update first-touch / last-touch attribution.
   *
   * First touch:
   *   Written once and then preserved.
   *
   * Last touch:
   *   Replaced whenever the visitor arrives with
   *   supported tracking parameters.
   */
  function updateAttribution() {
    const params = getTrackingParams();
    const stored = getStoredAttribution();

    if (!hasTrackingParams(params)) {
      return stored;
    }

    const touch = createTouch(params);

    if (!stored.first) {
      stored.first = touch;
    }

    stored.last = touch;

    saveStoredAttribution(stored);

    return stored;
  }

  /**
   * Add an attribute only when it has a value.
   *
   * This avoids writing unnecessary empty Shopify
   * cart attributes.
   */
  function addAttribute(attributes, key, value) {
    if (
      value !== undefined &&
      value !== null &&
      value !== ''
    ) {
      attributes[key] = String(value);
    }
  }

  /**
   * Convert attribution into Shopify cart attributes.
   */
  function buildCartAttributes(attribution) {
    const attributes = {};

    if (attribution.first) {
      const first = attribution.first;

      addAttribute(
        attributes,
        'zc_first_source',
        first.source
      );

      addAttribute(
        attributes,
        'zc_first_medium',
        first.medium
      );

      addAttribute(
        attributes,
        'zc_first_campaign',
        first.campaign
      );

      addAttribute(
        attributes,
        'zc_first_campaign_id',
        first.campaign_id
      );

      addAttribute(
        attributes,
        'zc_first_adset_id',
        first.adset_id
      );

      addAttribute(
        attributes,
        'zc_first_ad_id',
        first.ad_id
      );

      addAttribute(
        attributes,
        'zc_first_content',
        first.content
      );

      addAttribute(
        attributes,
        'zc_first_term',
        first.term
      );

      addAttribute(
        attributes,
        'zc_first_fbclid',
        first.fbclid
      );

      addAttribute(
        attributes,
        'zc_first_landing_page',
        first.landing_page
      );

      addAttribute(
        attributes,
        'zc_first_referrer',
        first.referrer
      );

      addAttribute(
        attributes,
        'zc_first_timestamp',
        first.timestamp
      );
    }

    if (attribution.last) {
      const last = attribution.last;

      addAttribute(
        attributes,
        'zc_last_source',
        last.source
      );

      addAttribute(
        attributes,
        'zc_last_medium',
        last.medium
      );

      addAttribute(
        attributes,
        'zc_last_campaign',
        last.campaign
      );

      addAttribute(
        attributes,
        'zc_last_campaign_id',
        last.campaign_id
      );

      addAttribute(
        attributes,
        'zc_last_adset_id',
        last.adset_id
      );

      addAttribute(
        attributes,
        'zc_last_ad_id',
        last.ad_id
      );

      addAttribute(
        attributes,
        'zc_last_content',
        last.content
      );

      addAttribute(
        attributes,
        'zc_last_term',
        last.term
      );

      addAttribute(
        attributes,
        'zc_last_fbclid',
        last.fbclid
      );

      addAttribute(
        attributes,
        'zc_last_landing_page',
        last.landing_page
      );

      addAttribute(
        attributes,
        'zc_last_referrer',
        last.referrer
      );

      addAttribute(
        attributes,
        'zc_last_timestamp',
        last.timestamp
      );
    }

    return attributes;
  }

  /**
   * Write attribution to the Shopify cart.
   */
  async function writeCartAttributes(attribution) {
    if (!attribution.first && !attribution.last) {
      return;
    }

    const attributes = buildCartAttributes(attribution);

    if (Object.keys(attributes).length === 0) {
      return;
    }

    try {
      const response = await fetch(
        window.Shopify.routes.root + 'cart/update.js',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            attributes: attributes
          })
        }
      );

      if (!response.ok) {
        throw new Error(
          'Cart attribute update failed: ' +
          response.status
        );
      }

      return await response.json();

    } catch (error) {
      console.error(
        'Zero Center attribution tracking:',
        error
      );
    }
  }

  /*
   * Run attribution tracking.
   */
  const attribution = updateAttribution();

  if (attribution.first || attribution.last) {
    writeCartAttributes(attribution);
  }
})();